import type { Node, TextNode } from "./ir";
import { collectTextNodes } from "./paginate";

// How the browser set every text block, measured so the PDF can reproduce it
// line for line. react-pdf breaks lines its own way (Knuth–Plass, shrinkable
// spaces, and it re-breaks any line ending in a forced newline unless the box
// has ~3pt to spare), and it places baselines from unrounded font metrics
// where browsers round them to pixels. So the PDF doesn't ask react-pdf to set
// paragraphs at all: each measured line becomes its own single-line text,
// placed at the line's measured start and baseline (see renderPdf.tsx).

/** One line of a measured block: where it starts in the text, and its pen position (pt, from the block's border box). */
export type MeasuredLine = { start: number; x: number; baseline: number };

/** How the browser laid out one text block (pt; positions relative to its border box). */
export type TextLayout = {
  /** Character offsets at which lines 2..n start. */
  breaks: number[];
  lines: MeasuredLine[];
  width: number;
  height: number;
};

const ascentCache = new Map<string, number>();
let probeHost: HTMLDivElement | null = null;

/**
 * The browser's ascent (px) for a font at a size: distance from the
 * baseline (found with a zero-size inline-block, which sits on it) to the
 * top of a text range's box. Probed once per family and size.
 */
function browserAscent(family: string, sizePx: string): number {
  const key = `${family}|${sizePx}`;
  const known = ascentCache.get(key);
  if (known !== undefined) return known;
  if (!probeHost) {
    probeHost = document.createElement("div");
    probeHost.setAttribute("aria-hidden", "true");
    Object.assign(probeHost.style, { position: "fixed", left: "-100000px", top: "0", visibility: "hidden", pointerEvents: "none" });
    document.body.appendChild(probeHost);
  }
  const line = document.createElement("div");
  Object.assign(line.style, { fontFamily: family, fontSize: sizePx, lineHeight: "normal", whiteSpace: "nowrap" });
  const textNode = document.createTextNode("H");
  const marker = document.createElement("span");
  Object.assign(marker.style, { display: "inline-block", width: "0", height: "0", verticalAlign: "baseline" });
  line.append(textNode, marker);
  probeHost.appendChild(line);
  const range = document.createRange();
  range.setStart(textNode, 0);
  range.setEnd(textNode, 1);
  const ascent = marker.getBoundingClientRect().bottom - range.getBoundingClientRect().top;
  line.remove();
  ascentCache.set(key, ascent);
  return ascent;
}

const PX_TO_PT = 72 / 96;
const WORD_JOINER = "⁠";

/**
 * Measures one rendered text block (an element marked `data-rt`). Character
 * offsets count the text's own characters: the word joiners the HTML
 * renderer inserts after hyphens are skipped. A character belongs to a new
 * line when its baseline (box top + the font's browser ascent) is lower than
 * the current line's — runs of different sizes share a baseline, so this
 * holds for mixed text too. Must run with the fonts loaded.
 */
export function measureTextBlock(el: Element): TextLayout {
  const box = el.getBoundingClientRect();
  const lines: MeasuredLine[] = [];
  const range = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let offset = 0;
  let lineBaseline: number | null = null;
  for (let t = walker.nextNode() as Text | null; t; t = walker.nextNode() as Text | null) {
    const s = t.data;
    const style = getComputedStyle(t.parentElement ?? (el as HTMLElement));
    const ascent = browserAscent(style.fontFamily, style.fontSize);
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === WORD_JOINER) continue;
      const logical = offset++;
      if (ch === " " || ch === "\n") continue;
      range.setStart(t, i);
      range.setEnd(t, i + 1);
      const r = range.getBoundingClientRect();
      if (r.height === 0) continue;
      const baseline = r.top + ascent;
      if (lineBaseline === null || baseline > lineBaseline + 1) {
        lines.push({ start: logical, x: (r.left - box.left) * PX_TO_PT, baseline: (baseline - box.top) * PX_TO_PT });
        lineBaseline = baseline;
      }
    }
  }
  return { breaks: lines.slice(1).map((l) => l.start), lines, width: box.width * PX_TO_PT, height: box.height * PX_TO_PT };
}

/**
 * Layouts of every text node of [nodes], rendered under [root], keyed by node
 * — page fragments reuse the same node objects, so the measurements follow
 * each block to whichever page it lands on.
 */
export function textLayoutByNode(root: Element, nodes: Node[], into = new Map<TextNode, TextLayout>()): Map<TextNode, TextLayout> {
  const list = collectTextNodes(nodes);
  const els = root.querySelectorAll("[data-rt]");
  if (els.length !== list.length) throw new Error(`Text layout mismatch: ${list.length} text nodes, ${els.length} rendered`);
  list.forEach((n, i) => into.set(n, measureTextBlock(els[i])));
  return into;
}

/** One line's share of each run: [run index, text] pairs (break spaces dropped). */
export type LineRuns = { run: number; text: string }[];

/**
 * Splits a text node's runs into its measured lines ([starts]: the character
 * offset where each line starts). The space a line wrapped at is dropped, as
 * is anything before the first line (leading spaces); a break inside a word
 * just splits the word.
 */
export function splitLines(n: TextNode, starts: number[]): LineRuns[] {
  if (!starts.length) return [];
  const lines: LineRuns[] = starts.map(() => []);
  let k = 0;
  let offset = 0;
  n.runs.forEach((r, ri) => {
    for (let i = 0; i < r.text.length; i++) {
      const at = offset + i;
      if (at < starts[0]) continue;
      while (k + 1 < starts.length && at >= starts[k + 1]) k++;
      if (r.text[i] === " " && k + 1 < starts.length && at + 1 === starts[k + 1]) continue;
      const line = lines[k];
      const last = line[line.length - 1];
      if (last && last.run === ri) last.text += r.text[i];
      else line.push({ run: ri, text: r.text[i] });
    }
    offset += r.text.length;
  });
  return lines;
}
